/**
 * @packageDocumentation
 * @category Util
 */

/**
 * Which dialogs are open, in the order they opened.
 *
 * Depth is open order, NOT React nesting, and that distinction is the whole
 * reason this is a module store rather than a context. A dialog opened from
 * inside another is almost never a child of it in the tree: the flags screen
 * renders the form and the SDK contract as SIBLINGS in one fragment, so a
 * context would report both at the same depth and the stack would stay as flat
 * as it was before.
 *
 * A module store also means the 62 call sites that render a `Dialog` need no
 * provider added above them for depth to start working.
 */
const openDialogs: symbol[] = [];
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

export const subscribeToDialogStack = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const openDialog = (id: symbol): void => {
  if (openDialogs.includes(id)) return;
  openDialogs.push(id);
  emit();
};

export const closeDialog = (id: symbol): void => {
  const index = openDialogs.indexOf(id);
  if (index < 0) return;
  openDialogs.splice(index, 1);
  emit();
};

/** How many dialogs are open beneath this one. `-1` while it is closed. */
export const getDialogDepth = (id: symbol): number => openDialogs.indexOf(id);

/** Test seam. The store outlives any single render, so a suite that opens
 *  dialogs across cases would otherwise carry depth from one into the next. */
export const resetDialogStack = (): void => {
  openDialogs.length = 0;
  emit();
};
